import './playlist-form.scss';
import {createRef, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {Playlist} from '../../backend/interfaces/playlist';
import {useOffice} from '../../contexts/office/context';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {ContentPage} from '../content-page/content-page';
import {SubmitButton, SubmitButtonState} from '../submit-button/submit-button';
import {Alert, Box, Fab, FormLabel, useTheme, Divider, Typography} from '@mui/material';
import {PageHeader} from '../page-header/page-header';
import {translations} from '../../i18n/translations';
import {LocalizedStringInput} from '../localized-string-input/localized-string-input';
import {LocalizedString} from '../../backend/interfaces/localized-string';
import {ColorInput} from '../color-input/color-input';
import {SystemButton} from '../../backend/interfaces/system-button';
import {SelectInput} from '../select-input/select-input';
import {PlaylistType} from '../../backend/enums/playlist-type';
import {playlistTypes} from '../../utils/data/playlist-types';
import {SmoothImage} from '../smooth-image/smooth-image';
import {appSizes} from '../../utils/constants/sizes';
import {ImageCropperDialog} from '../image-cropper-dialog/image-cropper-dialog';
import {MultipleSelectInput} from '../multiple-select-input/multiple-select-input';
import {RouterPrompt} from '../router-prompt/router-prompt';
import {regions} from '../../utils/data/regions';
import {HEX_COLOR_REGEX} from '../../utils/constants/regex';
import {PostPlaylist} from '../../backend/requests/post-playlist';
import {
  nullifyLocalizedString,
  sanitizeLocalizedString,
} from '../../utils/functions/sanitize-localized-string';
import {PlaylistsRepository} from '../../repositories/playlists';
import {PostStorageRequest} from '../../backend/requests/post-storage-request';
import {StorageRequestRepository} from '../../repositories/storage-request';
import {StorageType} from '../../backend/enums/storage-type';
import {StorageFileType} from '../../backend/enums/storage-file-type';
import {
  PlaylistImageStorageRequestData,
  StorageRequestResult,
} from '../../backend/interfaces/storage-request';
import {PutPlaylist} from '../../backend/requests/put-playlist';
import {ScreenPaths} from '../../enums/screen-path';
import {AppLanguageTag} from '../../i18n/types';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import i18n from 'i18next';
import {CaregiverMode} from '../../enums';
import {ReadOnlyTextField} from '../read-only-text-field/read-only-text-field';
import {LoadingContent} from '../loading-content/loading-content';
import {Modal} from '../modal/modal';

const ColorInputStartAdornment = (props: {color: string}) => {
  // Get theme
  const theme = useTheme();

  return (
    <Box
      className="playlist-form-color-input-start-adornment"
      sx={{
        backgroundColor: props.color,
        borderColor: theme.palette.action.disabled,
      }}
    />
  );
};

/**
 * Form to view, edit, remove, and create playlists.
 *
 * It uses the office context to get the playlist's information.
 *
 * Permissions are given by props.
 */
export const PlaylistForm = ({
  caregiverMode,
  canWrite,
  ...props
}: {
  caregiverMode: CaregiverMode;
  canWrite: boolean;
  redirectUrl: string;
  showPageTitle: boolean;
  showExitButton: boolean;
}) => {
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  let history = useHistory();
  const {t} = useTranslation();
  const theme = useTheme();

  // Hold state
  const [originalModel, setOriginalModel] = useState<Playlist | undefined>();
  const [model, setModel] = useState<Playlist | undefined>();
  const [imageFile, setImageFile] = useState<File | undefined>();
  const [imageCropperFile, setImageCropperFile] = useState<File | undefined>();

  // Buttons
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState<boolean>(false);

  // Form validation
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [nameError, setNameError] = useState<string | undefined>();
  const [showNameError, setShowNameError] = useState<boolean>(false);
  const [enabledColorError, setEnabledColorError] = useState<string | undefined>();
  const [showEnabledColorError, setShowEnabledColorError] = useState<boolean>(false);
  const [pressedColorError, setPressedColorError] = useState<string | undefined>();
  const [showPressedColorError, setShowPressedColorError] = useState<boolean>(false);

  // Dialogs
  const [deleteDialogOpened, setDeleteDialogOpened] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Ref
  const imageInputFileRef = createRef<any>();

  // Set model when we get one
  useEffect(() => {
    // Set the model
    const init = async () => {
      if (officeContext.state.playlistForm) {
        // Init buttons state
        officeContext.setPlaylistFormSubmitButtonState(SubmitButtonState.Normal);
        officeContext.setPlaylistFormDeleteButtonState(SubmitButtonState.Normal);

        // Init some state
        setDataChanged(false);
        setImageFile(undefined);

        // Set models
        setModel(officeContext.state.playlistForm);
        setOriginalModel(officeContext.state.playlistForm);
      }
    };

    // Init
    init();

    // Clean up
    return () => {
      // Log
      console.debug('🧹 Playlist form button states cleaned in office context.');

      // Clear button states
      officeContext.clearPlaylistFormSubmitButtonState();
      officeContext.clearPlaylistFormDeleteButtonState();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeContext.state.playlistForm]);

  // Form validation
  useEffect(() => {
    if (model && originalModel) {
      if (caregiverMode === CaregiverMode.SystemAdministrator) {
        // ----- Fields validation -----
        // Holds validation
        let nameValid: boolean = false;
        let enabledColorValid: boolean = false;
        let pressedColorValid: boolean = false;

        // Name validation
        if (model.name && model.name.trim().length > 0) {
          // No errors
          if (nameError !== undefined) {
            setNameError(undefined);
          }

          // Valid
          nameValid = true;
        } else {
          // Errors
          if (showNameError) {
            setNameError(
              model.id
                ? t(translations.playlist_form.errors.edit_form_validation_invalid_field)
                : t(translations.playlist_form.errors.create_form_validation_invalid_field)
            );
          }

          // Invalid
          nameValid = false;
        }

        // Enabled button color validation
        if (model.systemButton.enabledStateColor) {
          if (HEX_COLOR_REGEX.test(model.systemButton.enabledStateColor)) {
            // No errors
            if (enabledColorError !== undefined) {
              setEnabledColorError(undefined);
            }

            // Valid
            enabledColorValid = true;
          } else {
            // Errors
            if (showEnabledColorError) {
              setEnabledColorError(
                t(translations.playlist_form.errors.form_validation_invalid_color)
              );
            }

            // Invalid
            enabledColorValid = false;
          }
        } else {
          // Errors
          if (showEnabledColorError) {
            setEnabledColorError(
              model.id
                ? t(translations.playlist_form.errors.edit_form_validation_invalid_field)
                : t(translations.playlist_form.errors.create_form_validation_invalid_field)
            );
          }

          // Invalid
          enabledColorValid = false;
        }

        // Pressed button color validation
        if (model.systemButton.pressedStateColor) {
          if (HEX_COLOR_REGEX.test(model.systemButton.pressedStateColor)) {
            // No errors
            if (pressedColorError !== undefined) {
              setPressedColorError(undefined);
            }

            // Valid
            pressedColorValid = true;
          } else {
            // Errors
            if (showPressedColorError) {
              setPressedColorError(
                t(translations.playlist_form.errors.form_validation_invalid_color)
              );
            }

            // Invalid
            pressedColorValid = false;
          }
        } else {
          // Errors
          if (showPressedColorError) {
            setPressedColorError(
              model.id
                ? t(translations.playlist_form.errors.edit_form_validation_invalid_field)
                : t(translations.playlist_form.errors.create_form_validation_invalid_field)
            );
          }

          // Invalid
          pressedColorValid = false;
        }
        // ----- Fields validation -----

        // ----- Data changed -----
        let dataWasUpdated: boolean = false;

        if (
          model.name.trim() !== originalModel.name.trim() ||
          (model?.description || '').trim() !== (originalModel?.description || '').trim() ||
          model.systemButton.enabledStateColor.trim() !==
            originalModel.systemButton.enabledStateColor.trim() ||
          model.systemButton.pressedStateColor.trim() !==
            originalModel.systemButton.pressedStateColor.trim() ||
          (model.nameLocalized?.enUS || '').trim() !==
            (originalModel.nameLocalized?.enUS || '').trim() ||
          (model.nameLocalized?.esES || '').trim() !==
            (originalModel.nameLocalized?.esES || '').trim() ||
          (model.nameLocalized?.ptPT || '').trim() !==
            (originalModel.nameLocalized?.ptPT || '').trim() ||
          (model.nameLocalized?.ptBR || '').trim() !==
            (originalModel.nameLocalized?.ptBR || '').trim() ||
          (model.descriptionLocalized?.enUS || '').trim() !==
            (originalModel.descriptionLocalized?.enUS || '').trim() ||
          (model.descriptionLocalized?.esES || '').trim() !==
            (originalModel.descriptionLocalized?.esES || '').trim() ||
          (model.descriptionLocalized?.ptPT || '').trim() !==
            (originalModel.descriptionLocalized?.ptPT || '').trim() ||
          (model.descriptionLocalized?.ptBR || '').trim() !==
            (originalModel.descriptionLocalized?.ptBR || '').trim() ||
          model.type !== originalModel.type ||
          model.isActive !== originalModel.isActive ||
          model.isActiveForAllEntitiesByDefault !== originalModel.isActiveForAllEntitiesByDefault ||
          model.imageUrl !== originalModel.imageUrl ||
          (model.regionalLockout || []).join() !== (originalModel.regionalLockout || []).join()
        ) {
          // Set it
          setDataChanged(true);

          // Set
          dataWasUpdated = true;
        } else {
          // Set it
          setDataChanged(false);

          // Set
          dataWasUpdated = false;
        }
        // ----- Data changed -----

        // ----- Submit button validation -----
        if (model.id) {
          // ----> Edit <----
          if (nameValid && enabledColorValid && pressedColorValid && dataWasUpdated) {
            // Enable submit button
            setSubmitButtonDisabled(false);
          } else {
            // Disable submit button
            setSubmitButtonDisabled(true);
          }
        } else {
          // ----> Create <----
          if (nameValid && enabledColorValid && pressedColorValid) {
            // Enable submit button
            setSubmitButtonDisabled(false);
          } else {
            // Disable submit button
            setSubmitButtonDisabled(true);
          }
        }
        // ----- Submit button validation -----
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabledColorError,
    model,
    nameError,
    originalModel,
    pressedColorError,
    caregiverMode,
    showEnabledColorError,
    showNameError,
    showPressedColorError,
  ]);

  // Submit changes
  const handleSubmit = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || caregiverMode !== CaregiverMode.SystemAdministrator) {
      return;
    }

    try {
      // Update submit button state
      officeContext.setPlaylistFormSubmitButtonState(SubmitButtonState.Loading);

      // Disabled delete button
      setDeleteButtonDisabled(true);

      if (model.id && caregiverMode === CaregiverMode.SystemAdministrator) {
        // ---> Edit <---

        // Playlist image to upload
        let uploadedImageLinkingUrl: string | null;
        if (imageFile) {
          // Request body
          const requestBodyImageFile: PostStorageRequest<PlaylistImageStorageRequestData> = {
            type: StorageType.PlaylistImage,
            fileType: StorageFileType.JPG,
            data: {
              playlistId: model.id || '',
            },
          };

          // Some file was selected to be upload, request upload to the backend
          const storageImageRequestResult: StorageRequestResult<PlaylistImageStorageRequestData> =
            await StorageRequestRepository.instance.create<PlaylistImageStorageRequestData>(
              requestBodyImageFile
            );

          // Upload file
          uploadedImageLinkingUrl =
            await StorageRequestRepository.instance.upload<PlaylistImageStorageRequestData>(
              storageImageRequestResult,
              imageFile
            );
        } else {
          uploadedImageLinkingUrl = model.imageUrl || null;
        }

        // Request body
        const requestBody: PutPlaylist = {
          type: model.type,
          isActive: model.isActive,
          name: model.name.trim(),
          systemButton: model.systemButton,
          imageUrl: uploadedImageLinkingUrl,
          description: model.description?.trim() || null,
          regionalLockout: model.regionalLockout || null,
          nameLocalized: model.nameLocalized ? nullifyLocalizedString(model.nameLocalized) : null,
          descriptionLocalized: model.descriptionLocalized
            ? nullifyLocalizedString(model.descriptionLocalized)
            : null,
          isActiveForAllEntitiesByDefault:
            typeof model.isActiveForAllEntitiesByDefault === 'boolean'
              ? model.isActiveForAllEntitiesByDefault
              : null,
        };

        // Update playlist
        const updatedPlaylist = await PlaylistsRepository.instance.updatePlaylistById(
          model.id,
          requestBody
        );

        // Reset context
        officeContext.setPlaylistForm(updatedPlaylist);

        // Show toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.playlist_form.success.edit_success_message),
        });

        // Update submit button state
        officeContext.setPlaylistFormSubmitButtonState(SubmitButtonState.Success);

        // Enable delete button
        setDeleteButtonDisabled(false);
      } else if (!model.id && caregiverMode === CaregiverMode.SystemAdministrator) {
        // ---> Create <---

        // Request body
        // Note: we will set the imageUrl after the playlist is created.
        const requestBody: PostPlaylist = {
          type: model.type,
          isActive: model.isActive,
          name: model.name.trim(),
          systemButton: model.systemButton,
          description: model.description?.trim() || undefined,
          regionalLockout: model.regionalLockout || undefined,
          nameLocalized: model.nameLocalized
            ? sanitizeLocalizedString(model.nameLocalized)
            : undefined,
          descriptionLocalized: model.descriptionLocalized
            ? sanitizeLocalizedString(model.descriptionLocalized)
            : undefined,
          isActiveForAllEntitiesByDefault:
            typeof model.isActiveForAllEntitiesByDefault === 'boolean'
              ? model.isActiveForAllEntitiesByDefault
              : undefined,
        };

        // Create the playlist
        const newPlaylist = await PlaylistsRepository.instance.createPlaylist(requestBody);

        // Result
        let resultPlaylist: Playlist = {...newPlaylist};

        // Playlist image to upload
        let uploadedImageLinkingUrl: string | undefined;
        if (imageFile) {
          // If an error occurs, we can't fail the whole creation process because the playlist was previously created
          try {
            // Request body
            const requestBodyImageFile: PostStorageRequest<PlaylistImageStorageRequestData> = {
              type: StorageType.PlaylistImage,
              fileType: StorageFileType.JPG,
              data: {
                playlistId: newPlaylist.id || '',
              },
            };

            // Some file was selected to be upload, request upload to the backend
            const storageImageRequestResult: StorageRequestResult<PlaylistImageStorageRequestData> =
              await StorageRequestRepository.instance.create<PlaylistImageStorageRequestData>(
                requestBodyImageFile
              );

            // Upload file
            uploadedImageLinkingUrl =
              await StorageRequestRepository.instance.upload<PlaylistImageStorageRequestData>(
                storageImageRequestResult,
                imageFile
              );
          } catch (error) {
            // Error
            console.debug('🔴 Could not process the playlist image.', error);
          }
        }

        // Update the playlist if necessary
        if (uploadedImageLinkingUrl) {
          // If an error occurs, we can't fail the whole creation process because the playlist was previously created
          try {
            // Request body
            // Ignore all values except the required ones and the ones we want to update
            const secondRequestBody: PutPlaylist = {
              name: newPlaylist.name,
              type: newPlaylist.type,
              isActive: newPlaylist.isActive,
              systemButton: newPlaylist.systemButton,
              imageUrl: uploadedImageLinkingUrl,
            };

            // Edit playlist
            const updatedPlaylist = await PlaylistsRepository.instance.updatePlaylistById(
              newPlaylist.id || '',
              secondRequestBody
            );

            // Update the result
            resultPlaylist = {...updatedPlaylist};
          } catch (error) {
            // Error
            console.debug('🔴 Could not update the playlist after creating it.', error);
          }
        }

        // Wait some time just to make sure the button state was updated
        // so the router prompt won't appear when the history is updated
        setTimeout(() => {
          // Reset navigation to edit mode
          history.replace(
            props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
              ? `${props.redirectUrl}${newPlaylist.id}`
              : `${props.redirectUrl}/${newPlaylist.id}`
          );

          // Reset context
          officeContext.setPlaylistForm(resultPlaylist);

          // Show toast
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.playlist_form.success.create_success_message, {
              name: resultPlaylist.name,
            }),
          });

          // Update submit button state
          officeContext.setPlaylistFormSubmitButtonState(SubmitButtonState.Success);

          // Enable delete button
          setDeleteButtonDisabled(false);
        }, 500);
      } else {
        // Throw error
        throw new Error('Missing permissions.');
      }
    } catch (error) {
      // Log
      console.debug('🔴 Could not edit/create the playlist.', error);

      // Update submit button state
      officeContext.setPlaylistFormSubmitButtonState(SubmitButtonState.Error);

      // Enable delete button
      setDeleteButtonDisabled(false);
    }
  };

  // Delete playlist
  const handleDeletePlaylist = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || caregiverMode !== CaregiverMode.SystemAdministrator) {
      return;
    }
    setIsDeleting(true);
    try {
      // Disable submit button
      setSubmitButtonDisabled(true);

      // Update delete button state
      officeContext.setPlaylistFormDeleteButtonState(SubmitButtonState.Loading);

      // Delete the playlist
      if (originalModel.id) {
        await PlaylistsRepository.instance.deletePlaylistById(originalModel.id);
      } else {
        // Error
        throw new Error('Invalid values to delete the playlist.');
      }

      // Wait some time just to make sure the button state was updated
      // so the router prompt won't appear when the history is updated
      setTimeout(() => {
        setIsDeleting(false);
        setDeleteDialogOpened(false);
        // Navigate back
        history.replace(
          props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
            ? `${props.redirectUrl}?${ScreenPaths.PlaylistDeletedQuery}=${originalModel?.name}`
            : `${props.redirectUrl}/?${ScreenPaths.PlaylistDeletedQuery}=${originalModel?.name}`
        );
      }, 500);
    } catch (error: any) {
      // Log
      console.debug('🔴 Could not delete the playlist.', error);

      // Update delete button state
      officeContext.setPlaylistFormDeleteButtonState(SubmitButtonState.Error);
      setIsDeleting(false);
      setDeleteDialogOpened(false);
      setSubmitButtonDisabled(false);
    }
  };

  // Handle change text fields
  const handleChangeTextField = (
    field: 'name' | 'description',
    value: string | undefined | null
  ) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set it
    setModel({
      ...model,
      [field]: finalValue,
    } as Playlist);
  };

  // Handle change system button colors
  const handleChangeSystemButtonColors = (
    buttonType: keyof SystemButton,
    value: string | undefined | null
  ) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set it
    setModel({
      ...model,
      systemButton: {
        ...model?.systemButton,
        [buttonType]: finalValue,
      },
    } as Playlist);
  };

  // Get name and description
  let nameLocalizedReadOnly: string = '';
  let descriptionLocalizedReadOnly: string = '';
  switch (i18n.language) {
    case AppLanguageTag.ptPT:
      nameLocalizedReadOnly = model?.nameLocalized?.ptPT || model?.name || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.ptPT || model?.description || '';
      break;
    case AppLanguageTag.esES:
      nameLocalizedReadOnly = model?.nameLocalized?.esES || model?.name || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.esES || model?.description || '';
      break;
    case AppLanguageTag.glES:
      nameLocalizedReadOnly = model?.nameLocalized?.glES || model?.name || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.glES || model?.description || '';
      break;
    case AppLanguageTag.enUS:
      nameLocalizedReadOnly = model?.nameLocalized?.enUS || model?.name || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.enUS || model?.description || '';
      break;
    case AppLanguageTag.ptBR:
      nameLocalizedReadOnly = model?.nameLocalized?.ptBR || model?.name || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.ptBR || model?.description || '';
      break;
    default:
      nameLocalizedReadOnly = model?.name || '';
      descriptionLocalizedReadOnly = model?.description || '';
      break;
  }

  // Strings to use in the form
  let pageTitle: string = '';
  let routerDialogTitle: string = '';
  let routerDialogContent: string = '';
  let routerCancelToast: string = '';
  switch (caregiverMode) {
    case CaregiverMode.SystemAdministrator:
      pageTitle = originalModel?.id
        ? originalModel?.name || ''
        : t(translations.playlist_form.create_playlist_title);
      routerDialogTitle = model?.id
        ? t(translations.playlist_form.router_prompt_edit_title, {
            name: originalModel?.name || '',
          })
        : t(translations.playlist_form.router_prompt_create_title, {name: model?.name || ''});
      routerDialogContent = model?.id
        ? t(translations.playlist_form.router_prompt_edit_message)
        : t(translations.playlist_form.router_prompt_create_message);
      routerCancelToast = model?.id
        ? t(translations.playlist_form.edit_cancel_toast, {
            name: originalModel?.name || '',
          })
        : t(translations.playlist_form.create_cancel_toast);
      break;
    case CaregiverMode.Professional:
      pageTitle = nameLocalizedReadOnly;
      break;
    default:
      break;
  }

  return (
    <ContentPage>
      {model ? (
        <form>
          <Box className="playlist-form-container">
            {(props.showExitButton ||
              props.showPageTitle ||
              caregiverMode === CaregiverMode.SystemAdministrator) && (
              <PageHeader title={props.showPageTitle ? pageTitle : undefined} />
            )}
            {canWrite && (
              <LocalizedStringInput
                defaultValueLabel={t(translations.playlist_form.default_name_input)}
                defaultValue={model.name || ''}
                localizedValuesLabel={t(translations.playlist_form.name_translations_input)}
                localizedValues={model.nameLocalized}
                handleChangeDefaultValue={(value) => handleChangeTextField('name', value)}
                handleChangeLocalizedValues={(values: LocalizedString) =>
                  setModel({
                    ...model,
                    nameLocalized: values,
                  } as Playlist)
                }
                handleBlurDefaultValue={() => {
                  if (!showNameError) {
                    setShowNameError(true);
                  }
                }}
                defaultValueError={nameError}
                defaultValueRequired={true}
                disabled={
                  officeContext.state.playlistFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.playlistFormDeleteButtonState === SubmitButtonState.Loading
                }
              />
            )}
            {!canWrite && (
              <ReadOnlyTextField
                className="playlist-form-line-1"
                label={t(translations.playlist_form.default_name_input)}
                value={nameLocalizedReadOnly}
                disabledTooltip={t(translations.shared.input_disabled_tooltip)}
              />
            )}
            {canWrite && (
              <Box className="playlist-form-line-2">
                <SelectInput
                  id="playlist-form-type-select"
                  className="playlist-form-line-2-left"
                  label={t(translations.playlist_form.type_input)}
                  value={model.type}
                  handleChange={(value) =>
                    setModel({
                      ...model,
                      type: value as PlaylistType,
                    })
                  }
                  required={true}
                  disabled={
                    officeContext.state.playlistFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.playlistFormDeleteButtonState === SubmitButtonState.Loading
                  }
                  items={playlistTypes.map((obj) => {
                    return {label: t(obj.translation), value: obj.type};
                  })}
                />
                <SelectInput
                  id="playlist-form-state-select"
                  className="playlist-form-line-2-right"
                  label={t(translations.playlist_form.state_input)}
                  value={model.isActive.toString()}
                  handleChange={(value) =>
                    setModel({
                      ...model,
                      isActive: JSON.parse(value),
                    })
                  }
                  required={true}
                  disabled={
                    officeContext.state.playlistFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.playlistFormDeleteButtonState === SubmitButtonState.Loading
                  }
                  items={[
                    {value: 'true', label: t(translations.playlist_form.state_value_true)},
                    {value: 'false', label: t(translations.playlist_form.state_value_false)},
                  ]}
                />
              </Box>
            )}
            {!canWrite && (
              <Box className="playlist-form-line-2">
                <ReadOnlyTextField
                  className="playlist-form-line-2-left"
                  label={t(translations.playlist_form.type_input)}
                  value={t(translations.playlist_types[model.type]) || ''}
                  disabledTooltip={t(translations.shared.input_disabled_tooltip)}
                />
                <ReadOnlyTextField
                  className="playlist-form-line-2-right"
                  label={t(translations.playlist_form.state_input)}
                  value={
                    model.isActive === true
                      ? t(translations.playlist_form.state_value_true)
                      : t(translations.playlist_form.state_value_false)
                  }
                  disabledTooltip={t(translations.shared.input_disabled_tooltip)}
                />
              </Box>
            )}
            {canWrite && (
              <Box className="playlist-form-line-3">
                <ColorInput
                  className="playlist-form-line-3-left"
                  label={t(translations.playlist_form.enabled_state_color_input)}
                  value={model.systemButton.enabledStateColor}
                  handleChange={(value) =>
                    handleChangeSystemButtonColors('enabledStateColor', value)
                  }
                  handleBlur={() => {
                    if (!showEnabledColorError) {
                      setShowEnabledColorError(true);
                    }
                  }}
                  error={enabledColorError}
                  disabled={
                    officeContext.state.playlistFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.playlistFormDeleteButtonState === SubmitButtonState.Loading
                  }
                  required={true}
                />
                <ColorInput
                  className="playlist-form-line-3-right"
                  label={t(translations.playlist_form.pressed_state_color_input)}
                  value={model.systemButton.pressedStateColor}
                  handleChange={(value) =>
                    handleChangeSystemButtonColors('pressedStateColor', value)
                  }
                  handleBlur={() => {
                    if (!showPressedColorError) {
                      setShowPressedColorError(true);
                    }
                  }}
                  error={pressedColorError}
                  disabled={
                    officeContext.state.playlistFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.playlistFormDeleteButtonState === SubmitButtonState.Loading
                  }
                  required={true}
                />
              </Box>
            )}
            {!canWrite && (
              <Box className="playlist-form-line-3">
                <ReadOnlyTextField
                  className="playlist-form-line-3-left"
                  label={t(translations.playlist_form.enabled_state_color_input)}
                  value={model.systemButton.enabledStateColor || ''}
                  startAdornment={
                    <ColorInputStartAdornment color={model.systemButton.enabledStateColor} />
                  }
                  disabledTooltip={t(translations.shared.input_disabled_tooltip)}
                  disabled
                />
                <ReadOnlyTextField
                  className="playlist-form-line-3-right"
                  label={t(translations.playlist_form.pressed_state_color_input)}
                  value={model.systemButton.pressedStateColor || ''}
                  startAdornment={
                    <ColorInputStartAdornment color={model.systemButton.pressedStateColor} />
                  }
                  disabledTooltip={t(translations.shared.input_disabled_tooltip)}
                />
              </Box>
            )}
            {canWrite && (
              <LocalizedStringInput
                className="playlist-form-line-4"
                defaultValueLabel={t(translations.playlist_form.default_description_input)}
                defaultValue={model.description || ''}
                localizedValuesLabel={t(translations.playlist_form.description_translations_input)}
                localizedValues={model.descriptionLocalized}
                handleChangeDefaultValue={(value) => handleChangeTextField('description', value)}
                handleChangeLocalizedValues={(values: LocalizedString) =>
                  setModel({
                    ...model,
                    descriptionLocalized: values,
                  } as Playlist)
                }
                defaultValueRequired={false}
                disabled={
                  officeContext.state.playlistFormSubmitButtonState === SubmitButtonState.Loading ||
                  officeContext.state.playlistFormDeleteButtonState === SubmitButtonState.Loading
                }
                multiline={true}
              />
            )}
            {!canWrite && (
              <ReadOnlyTextField
                className="playlist-form-line-4"
                label={t(translations.playlist_form.default_description_input)}
                value={descriptionLocalizedReadOnly}
                disabledTooltip={t(translations.shared.input_disabled_tooltip)}
              />
            )}
            {canWrite && (
              <Box className="playlist-form-line-5">
                <MultipleSelectInput
                  id="playlist-form-region-lockout-multiple-select"
                  className="playlist-form-line-5-left"
                  label={t(translations.playlist_form.regional_lockout_input)}
                  placeholder={t(translations.playlist_form.regional_lockout_placeholder)}
                  value={model.regionalLockout}
                  clearable={true}
                  disabled={
                    officeContext.state.playlistFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.playlistFormDeleteButtonState === SubmitButtonState.Loading
                  }
                  handleChange={(value?: Array<string>) =>
                    setModel({
                      ...model,
                      regionalLockout: value,
                    } as Playlist)
                  }
                  items={regions.map((obj) => {
                    return {label: t(obj.translation), value: obj.type};
                  })}
                />
                <SelectInput
                  id="playlist-form-active-by-default-select"
                  className="playlist-form-line-5-right"
                  label={t(translations.playlist_form.active_by_default_for_all_entities_input)}
                  placeholder={t(
                    translations.playlist_form.active_by_default_for_all_entities_placeholder_input
                  )}
                  value={
                    typeof model.isActiveForAllEntitiesByDefault === 'boolean'
                      ? model.isActiveForAllEntitiesByDefault.toString()
                      : undefined
                  }
                  clearable={true}
                  disabled={
                    officeContext.state.playlistFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.playlistFormDeleteButtonState === SubmitButtonState.Loading
                  }
                  handleChange={(value) =>
                    setModel({
                      ...model,
                      isActiveForAllEntitiesByDefault: value ? JSON.parse(value) : undefined,
                    })
                  }
                  items={[
                    {value: 'true', label: t(translations.playlist_form.yes_value)},
                    {value: 'false', label: t(translations.playlist_form.no_value)},
                  ]}
                />
              </Box>
            )}
            {!canWrite && (
              <Box className="playlist-form-line-5">
                <ReadOnlyTextField
                  className="playlist-form-line-5-flex"
                  label={t(translations.playlist_form.regional_lockout_input)}
                  value={
                    model.regionalLockout && model.regionalLockout.length > 0
                      ? model.regionalLockout
                          .map((region) => t(translations.regions[region]))
                          .join(', ')
                      : t(translations.user_form.not_specified)
                  }
                  disabledTooltip={t(translations.shared.input_disabled_tooltip)}
                />
              </Box>
            )}
            {canWrite && (
              <Box className="playlist-form-line-6">
                <FormLabel
                  className="playlist-form-image-label"
                  component="legend"
                  disabled={
                    officeContext.state.playlistFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.playlistFormDeleteButtonState === SubmitButtonState.Loading
                  }
                >
                  {t(translations.playlist_form.image_url_input)}
                </FormLabel>
                <SmoothImage
                  src={model.imageUrl || ''}
                  width={appSizes.profileImageSize.width}
                  height={appSizes.profileImageSize.height}
                  imageWidth={appSizes.profileImageSize.width}
                  imageHeight={appSizes.profileImageSize.height}
                  borderRadius={appSizes.appButtonSize.borderRadius}
                />
                <Box
                  className="playlist-form-image-alert"
                  sx={{
                    maxWidth: `${appSizes.profileImageSize.width}px`,
                    width: '100%',
                  }}
                >
                  <Alert severity="info">{t(translations.playlist_form.image_info_message)}</Alert>
                </Box>
                <Box
                  className="playlist-form-image-edit-button"
                  sx={{
                    maxWidth: `${appSizes.profileImageSize.width}px`,
                    width: '100%',
                  }}
                >
                  {model.imageUrl && (
                    <Fab
                      // Override with error color because it's not in the color prop
                      sx={{
                        backgroundColor: theme.palette.error.main,
                        '&:hover': {
                          backgroundColor: theme.palette.error.dark,
                        },
                        marginRight: '8px',
                      }}
                      disabled={
                        officeContext.state.playlistFormSubmitButtonState ===
                          SubmitButtonState.Loading ||
                        officeContext.state.playlistFormDeleteButtonState ===
                          SubmitButtonState.Loading
                      }
                      size="small"
                      color="primary"
                      onClick={() => {
                        setModel({
                          ...model,
                          imageUrl: undefined,
                        } as Playlist);
                        setImageFile(undefined);
                      }}
                    >
                      <DeleteIcon />
                    </Fab>
                  )}
                  <input
                    ref={imageInputFileRef}
                    disabled={
                      officeContext.state.playlistFormSubmitButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.playlistFormDeleteButtonState ===
                        SubmitButtonState.Loading
                    }
                    style={{display: 'none'}}
                    accept="image/jpeg"
                    type="file"
                    value=""
                    onChange={(event: any) => {
                      if (event.target.files[0]) {
                        setImageCropperFile(event.target.files[0]);
                      }
                    }}
                  />
                  <Fab
                    disabled={
                      officeContext.state.playlistFormSubmitButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.playlistFormDeleteButtonState ===
                        SubmitButtonState.Loading
                    }
                    size="small"
                    color="primary"
                    onClick={() => imageInputFileRef.current.click()}
                  >
                    <EditIcon />
                  </Fab>
                </Box>
              </Box>
            )}
            {!canWrite && (
              <Box className="playlist-form-line-6">
                <FormLabel className="playlist-form-image-label" component="legend">
                  {t(translations.playlist_form.image_url_input)}
                </FormLabel>
                <SmoothImage
                  src={model.imageUrl || ''}
                  width={appSizes.profileImageSize.width}
                  height={appSizes.profileImageSize.height}
                  imageWidth={appSizes.profileImageSize.width}
                  imageHeight={appSizes.profileImageSize.height}
                  borderRadius={appSizes.appButtonSize.borderRadius}
                />
              </Box>
            )}
          </Box>
          {canWrite && imageCropperFile && (
            <ImageCropperDialog
              open={true}
              imageFile={imageCropperFile}
              handleClose={(result?: File) => {
                setImageCropperFile(undefined);

                if (result) {
                  setImageFile(result);
                  const reader = new FileReader();
                  reader.onload = () => {
                    setModel({
                      ...model,
                      imageUrl: reader.result as string, // base64
                    } as Playlist);
                  };
                  reader.readAsDataURL(result);
                }
              }}
              aspect={appSizes.profileImageSize.width / appSizes.profileImageSize.height}
              size={`${appSizes.profileImageSize.width}x${appSizes.profileImageSize.height}`}
            />
          )}
        </form>
      ) : (
        <LoadingContent sx={{minHeight: '60vh'}} />
      )}
      {/* Delete button and dialog */}
      {caregiverMode === CaregiverMode.SystemAdministrator && (
        <>
          <Box className="playlist-form-delete-container">
            <Divider />
            <Box className="playlist-form-delete-button-container">
              {model && model.id && (
                <SubmitButton
                  disabled={deleteButtonDisabled}
                  state={
                    officeContext.state.playlistFormDeleteButtonState || SubmitButtonState.Normal
                  }
                  onStateChange={(newState: SubmitButtonState) =>
                    officeContext.setPlaylistFormDeleteButtonState(newState)
                  }
                  normalStateLabel={t(translations.playlist_form.delete_button)}
                  loadingStateLabel={t(translations.playlist_form.deleting_button)}
                  successStateLabel={t(translations.playlist_form.success.delete_success_button)}
                  errorStateLabel={t(translations.playlist_form.errors.error_on_delete_button)}
                  endIcon={<DeleteIcon />}
                  type="button"
                  variant="outlined"
                  color="error"
                  onClick={(event: any) => {
                    event.preventDefault();
                    setDeleteDialogOpened(true);
                  }}
                />
              )}
              <SubmitButton
                disabled={submitButtonDisabled}
                state={
                  officeContext.state.playlistFormSubmitButtonState || SubmitButtonState.Normal
                }
                onStateChange={(newState: SubmitButtonState) =>
                  officeContext.setPlaylistFormSubmitButtonState(newState)
                }
                normalStateLabel={t(translations.playlist_form.save_button)}
                loadingStateLabel={t(translations.playlist_form.saving_button)}
                successStateLabel={t(translations.playlist_form.success.save_success_button)}
                errorStateLabel={t(translations.playlist_form.errors.error_on_save_button)}
                endIcon={<SaveIcon />}
                onClick={handleSubmit}
                sx={{
                  width: '100%',
                  maxWidth: '252px',
                }}
              />
            </Box>
          </Box>
          <Modal
            open={deleteDialogOpened}
            maxWidth="sm"
            fullWidth
            className="associate-device-form-modal"
            preventCloseClick
            onClose={() => setDeleteDialogOpened(false)}
            actions={[
              {
                label: t(translations.modal.cancel),
                type: 'destructive',
                onClick: () => setDeleteDialogOpened(false),
                disabled: isDeleting,
              },
              {
                label: t(translations.modal.confirm),
                type: 'nondestructive',
                onClick: handleDeletePlaylist,
                loading: isDeleting,
                disabled: isDeleting,
              },
            ]}
          >
            <Typography className="title">
              {t(translations.playlist_form.delete_dialog_title, {
                name: originalModel?.name || '',
              })}
            </Typography>
            <Typography className="subtitle">
              {t(translations.playlist_form.delete_dialog_content)}
            </Typography>
          </Modal>
        </>
      )}
      {/* Router */}
      {model && canWrite && (
        <RouterPrompt
          // Show prompt if the data changed but make sure the path
          // is not changing because the playlist was deleted/created
          when={
            dataChanged &&
            officeContext.state.playlistFormDeleteButtonState !== SubmitButtonState.Loading &&
            officeContext.state.playlistFormSubmitButtonState !== SubmitButtonState.Loading
          }
          subtitle={routerDialogTitle}
          message={routerDialogContent}
          onOK={() => true}
          onCancel={() => false}
          toastNotification={{
            severity: 'info',
            message: routerCancelToast,
          }}
        />
      )}
    </ContentPage>
  );
};
